import PrecisionService from '@core/services/PrecisionService';

import type { IProposalMedicalInsured, IProposalResponse } from '../types';
import {
  getGenericLine,
  getLineCategory,
  hasMedical,
  hasProperty,
  hasRisk
} from '@utils/helper-utils';
import { GenericLines, LineCategory } from 'src/enums';

export const pct = (v: unknown): number | null =>
  typeof v === 'number' && !Number.isNaN(v)
    ? v / 100
    : v == null || v === ''
      ? null
      : Number(v) / 100;

export const num = (v: unknown): number =>
  typeof v === 'number' && !Number.isNaN(v)
    ? v
    : v == null || v === ''
      ? 0
      : Number(v);

export const numOrNull = (v: unknown): number | null =>
  v == null || v === '' ? null : Number(v);

const boolOrNull = (v: unknown): boolean | null => {
  if (typeof v === 'boolean') return v;
  if (v == null || v === '') return null;
  const s = String(v).toLowerCase();
  if (s === 'true' || s === 'yes' || s === '1') return true;
  if (s === 'false' || s === 'no' || s === '0') return false;

  return null;
};

function pickIdAddress(
  node: any,
  idKeys: string[],
  addrKeys: string[]
): { id?: string; address?: string } {
  const id =
    idKeys.map((k) => String(node?.[k] ?? '').trim()).find(Boolean) ||
    undefined;
  const address =
    addrKeys.map((k) => String(node?.[k] ?? '').trim()).find(Boolean) ||
    undefined;

  return { id, address };
}

export function buildSaveDraftVariables(
  pd: IProposalResponse,
  values: any,
  lineCode: string,
  routeId?: string
) {
  const id =
    (routeId && routeId.trim()) ||
    (pd as any)?.id ||
    (pd?.summaryDetails as any)?.id ||
    '';

  const isMarine = getGenericLine(lineCode) === GenericLines.MARINE;
  const isMarineHull = getGenericLine(lineCode) === GenericLines.MARINE_HULL;
  const isExpat = getLineCategory(lineCode) === LineCategory.Expat;
  const isProperty = hasProperty(lineCode);
  const isMedical = getLineCategory(lineCode) === LineCategory.Medical;
  const isGroupMedical =
    getGenericLine(lineCode) === GenericLines.MEDICAL_GROUP;
  const isMedicalOrTravel = hasMedical(lineCode);
  const isMotor = getLineCategory(lineCode) === LineCategory.Motor;
  const isTravel = getLineCategory(lineCode) === LineCategory.Travel;
  const isMotorAllRisk = lineCode === '51';
  const isRisk = hasRisk(lineCode);

  const policyCurrencyID = values.policyCurrency.id;
  const policyEffectiveDate = new Date(values.policyEffectiveDate).toISOString();
  const policyEndDate = values.policyEndDate ? new Date(values.policyEndDate).toISOString() : null;
  const policyIssueDate = new Date(values.policyIssueDate).toISOString();

  const persons: any[] = [];

  const payer = values.payerOwner?.payer ?? {};
  const owner = values.payerOwner?.owner ?? {};
  const introducedBy = values.payerOwner?.introducedBy ?? {};

  const payerPick = pickIdAddress(payer, ['id', 'id'], ['address', 'address']);
  const ownerPick = pickIdAddress(
    owner,
    ['ownerId', 'id'],
    ['ownerAddress', 'address']
  );
  const introPick = pickIdAddress(
    introducedBy,
    ['id'],
    ['addressId', 'address']
  );

  if (payerPick.id) {
    persons.push({
      personID: payerPick.id,
      personAddressID: payerPick.address || '',
      type: 'PAYER',
      order: 1
    });

    const isDifferent = Boolean(values.payerOwner?.ownerIsDifferent);
    const ownerIdToUse =
      isDifferent && ownerPick.id ? ownerPick.id : payerPick.id;
    const ownerAddrToUse =
      isDifferent && ownerPick.id
        ? ownerPick.address || ''
        : payerPick.address || '';

    persons.push({
      personID: ownerIdToUse,
      personAddressID: ownerAddrToUse,
      type: 'OWNER',
      order: 1
    });
  }

  if (introPick.id) {
    persons.push({
      personID: introPick.id,
      personAddressID: null,
      type: 'INTRODUCED_BY',
      order: 1
    });
  }

  if (isMedicalOrTravel) {
    values.medicalInsureds.forEach(
      (medicalInsured: IProposalMedicalInsured, idx: number) => {
        if (!medicalInsured?.insuredID) return; // ⬅️ skip invalid entries

        persons.push({
          personID: medicalInsured.insuredID,
          personAddressID: medicalInsured.addressID || '',
          order: idx + 1,
          isPrincipal: medicalInsured.isPrincipal,
          cardNumber: medicalInsured.cardNumber,
          passportNumber: medicalInsured.passportNumber,
          continuity:
            medicalInsured.continuity !== '' ? medicalInsured.continuity : null,
          exclusions: medicalInsured.exclusions,
          remarks: medicalInsured.remarks,
          relation: medicalInsured.relation,
          grApplicable: medicalInsured.isPrincipal
            ? true
            : medicalInsured.grApplicable === 'YES',
          grLimitDays: 0,
          tpaReference1: medicalInsured.tpaReference1,
          tpaReference2: medicalInsured.tpaReference2,
          dateOfBirth: medicalInsured.dateOfBirth,
          type: 'INSURED'
        });
      }
    );
  } else {
    (values.insuredPerson || []).forEach((p: any, idx: number) => {
      const ip = pickIdAddress(
        p,
        ['id', 'personId'],
        ['address', 'personAddress']
      );
      if (ip.id) {
        const parsedOrder =
          p.order !== undefined &&
          p.order !== null &&
          `${p.order}`.trim() !== ''
            ? Number(p.order)
            : idx + 1;

        persons.push({
          personID: ip.id,
          personAddressID: ip.address || '',
          type: 'INSURED',
          order: Number.isFinite(parsedOrder) ? parsedOrder : idx + 1,
          coverLimit: p.limitOfCover != null ? Number(p.limitOfCover) : null
        });
      }
    });
  }

  // -------------------- EXPAT: beneficiaries for persons --------------------
  if (isExpat || isMedicalOrTravel || isMotorAllRisk) {
    // Prefer top-level beneficiaries; fallback to beneficiaries defined under insured persons
    const beneficiaries: any[] =
      Array.isArray(values?.beneficiaries) && values.beneficiaries.length
        ? values.beneficiaries
        : (values.insuredPerson || []).flatMap((ip: any) =>
            Array.isArray(ip?.beneficiaries) ? ip.beneficiaries : []
          );

    beneficiaries.forEach((b: any, idx: number) => {
      const bp = pickIdAddress(
        b,
        ['id', 'personId'],
        ['address', 'personAddress']
      );
      if (!bp.id) return;

      const parsedOrder =
        b.order !== undefined && b !== null && `${b.order ?? ''}`.trim() !== ''
          ? Number(b.order)
          : idx + 1;

      const rawPct = b.beneficiaryPercentage ?? b.percentage ?? b.share ?? null;
      const benePct = rawPct != null ? pct(rawPct) : null; // convert 0–100 to 0–1

      persons.push({
        personID: bp.id,
        personAddressID: bp.address || '',
        type: 'BENEFICIARY',
        order: Number.isFinite(parsedOrder) ? parsedOrder : idx + 1,
        beneficiaryPercentage: benePct != null ? benePct : null
      });
    });
  }

  const policyPlanCoverInputs = (values.covers || [])
    .filter((c: any) => !!c.policyCover)
    .map((c: any) => {
      const isAmt = c.coverPremiumType === 'AMOUNT';

      return {
        planCoverID: c.policyCover,
        isMain: !!c.isMain,
        isMandatory: !!c.isMandatory,
        isEditable: !!c.isEditable,
        effectiveFrom: c.coverEffectiveFrom,
        effectiveTo: c.coverEffectiveTo,
        sumInsured: num(c.coverSumInsured),
        sumInsuredIsAdditive: !!c.sumInsuredIsAdditive,
        premiumType: c.coverPremiumType || null,
        premiumAmount: isAmt ? numOrNull(c.coverPremiumAmount) : null,
        premiumPercentage:
          PrecisionService.divideBy100(c.coverPremiumPercentage) ?? null,
        excessOnClaimType: c.excessOnClaimType || null,
        excessOnClaimAmount:
          c.excessOnClaimType === 'AMOUNT'
            ? numOrNull(c.excessOnClaimAmount)
            : null,
        excessOnClaimPercentage:
          c.excessOnClaimType === 'PERCENTAGE'
            ? pct(c.excessOnClaimPercentage)
            : null,
        excessOnClaimDays:
          c.excessOnClaimType === 'DAYS' ? num(c.excessOnClaimDays) : null
      };
    });

  const policyQuoteInput = {
    policyCost: num(values.chargesAndCost?.policyCost),
    tpaTypes: values.chargesAndCost?.TPAFeesType ?? null,
    tpaFeesAmount: numOrNull(values.chargesAndCost?.TPAFeesAmount),
    tpaFeePercentage:
      values.chargesAndCost?.TPAFeesPercentage != null
        ? pct(values.chargesAndCost?.TPAFeesPercentage)
        : 0,
    tpaApplicableOnOptions: values.chargesAndCost?.TPAFeesApplicableOn ?? null,
    chargesPercentage:
      values.chargesAndCost?.chargesPercentage != null
        ? pct(values.chargesAndCost.chargesPercentage)
        : 0,
    initialDiscount: Number(values?.chargesAndCost?.discount) ?? null
  };

  const policyMarineInputs = isMarine
    ? {
        matter: values.marineDetails?.matter || null,
        description: values.marineDetails?.description || null,
        vessalName: values.marineDetails?.vesselName || null,
        vessalAge: numOrNull(values.marineDetails?.vesselAge),
        departureFrom: values.marineDetails?.from || null,
        destinationTo: values.marineDetails?.to || null,
        transportationVia: values.marineDetails?.via || null,
        survey: values.marineDetails?.survey || null,
        cargoContract: values.marineDetails?.cargoContract || null,
        lcNumber: values.marineDetails?.lcNumber || null,
        lcIssueDate: values.marineDetails?.lcIssueDate || null,
        bank: values.marineDetails?.bank || null,
        claimsPayableBy: values.marineDetails?.claimsPayableBy || null,
        loading:
          values.marineDetails?.loading != null
            ? pct(values.marineDetails.loading)
            : null,
        remarks: values.marineDetails?.remarks || null,
        sumPerShipment: numOrNull(values.marineDetails?.sumPerShipment),
        sumPerShipmentCurrency:
          values.marineDetails?.sumPerShipmentCurrency || null,
        sumPerShipmentRate:
          values.marineDetails?.sumPerShipmentRate != null
            ? values.marineDetails?.sumPerShipmentRate
            : null,
        sumPerShipmentPolicyCurrency:
          values.marineDetails?.sumPerShipmentPolicyCurrency != null
            ? Number(values.marineDetails?.sumPerShipmentPolicyCurrency)
            : null
      }
    : undefined;

  const policyMarineHullInputs = isMarineHull
    ? {
        vesselName: values.marineHullDetails?.vesselName || null,
        connectionPort: values.marineHullDetails?.connectionPort || null,
        territorialWaters: values.marineHullDetails?.territorialWaters || null,
        usage: values.marineHullDetails?.usage || null,
        builder: values.marineHullDetails?.builder || null,
        constructionYear: values.marineHullDetails?.constructionYear || null,
        registryPort: values.marineHullDetails?.registeryPort || null,
        length: numOrNull(values.marineHullDetails?.length),
        weight: numOrNull(values.marineHullDetails?.weight),
        height: numOrNull(values.marineHullDetails?.height),
        engineType: values.marineHullDetails?.engineType || null,
        engineNumber: values.marineHullDetails?.engineNumber || null,
        engineYearBuilt: values.marineHullDetails?.engineYearBuilt || null,
        engineHorsePower: numOrNull(values.marineHullDetails?.engineHorsePower),
        engineMaxDesignedSpeed: numOrNull(
          values.marineHullDetails?.engineMaxDesignedSpeed
        ),
        engineFuel: values.marineHullDetails?.engineFuel || null,
        remarks: values.marineHullDetails?.remarks || null
      }
    : undefined;

  const policyBankersBlanketBondInputs =
    Array.isArray(values.bankersDetails) && values.bankersDetails.length
      ? values.bankersDetails.map((b: any, idx: number) => ({
          branchName: b.branchName ?? null,
          isMain:
            typeof b.isMain === 'string'
              ? b.isMain.toLowerCase() === 'yes'
              : (b.isMain ?? null),
          isAgency:
            typeof b.isAgency === 'string'
              ? b.isAgency.toLowerCase() === 'yes'
              : (b.isAgency ?? null),
          numberOfEmployees: numOrNull(b.employeeNumber),
          underwritingYear: numOrNull(b.underwritingYear),
          infidelity: numOrNull(b.infedility),
          burglary: numOrNull(b.burglary),
          holdup: numOrNull(b.holdup),
          inSafe: numOrNull(b.inSafe),
          inTransit: numOrNull(b.inTransit),
          forgedCheques: numOrNull(b.forgedCheques),
          counterFeitCurrencies: numOrNull(b.counterfeitCurrencies),
          clause6: numOrNull(b.clause6),
          falsification: numOrNull(b.falsification),
          order: idx + 1
        }))
      : undefined;

  const policyPropertyInputs = isProperty
    ? values.propertyRiskDetails.map((r: any) => ({
        order: r.riskOrder || null,
        riskDescription: r.riskDescription || null,
        location: r.location || null,
        geoLocationID: r.geoLocation || null,
        premium: (r.planCovers || []).reduce((acc: number, p: any) => {
          const v = numOrNull(p.premium) ?? 0;

          return acc + v;
        }, 0),
        sumInsured: (r.planCovers || []).reduce((acc: number, p: any) => {
          const v = numOrNull(p.sumInsured) ?? 0;

          return acc + v;
        }, 0),
        planCoverPropertyDetails: (r.planCovers || []).map((p: any) => ({
          planCoverID: p.planCoverID,
          riskRate: numOrNull(p.riskRate) ?? 0,
          sumInsured: numOrNull(p.sumInsured) ?? 0,
          premium: numOrNull(p.premium) ?? 0
        }))
      }))
    : undefined;

  const genericArray =
    values.descriptionOfRiskDetails || values.genericRiskDetails || [];
  const policyGenericInputs = isRisk
    ? genericArray.map((d: any) => ({
        riskDescription: d.descriptionOfRisk || null,
        geoLocationID: d.geoLocation || null,
        order: Number(d.riskOrder)
      }))
    : undefined;

  const expatPolicy = values.expat ?? values.expatDetails ?? {};
  const expatInsured = values.insuredExpat ?? values.expatInsuredDetails ?? {};

  const policyExpatInputs = isExpat
    ? {
        medicalClassID:
          expatPolicy.medicalClassID ?? expatPolicy.medicalClass ?? null,
        emergency: expatPolicy.emergency ?? null,
        continuity: boolOrNull(expatPolicy.continuity),
        continuityNumber: expatPolicy.continuityNumber ?? null,
        exclusion: expatPolicy.exclusion ?? null,
        remarks: expatPolicy.remarks ?? null,
        nationalityID:
          expatInsured.nationalityID ?? expatInsured.nationality ?? null,
        dateOfBirth: expatInsured.dateOfBirth ?? null,
        occupationClassID:
          expatInsured.occupationClassID ??
          expatInsured.occupationClass ??
          null,
        gender: expatInsured.gender ?? null,
        profession: expatInsured.profession ?? null,
        passportNumber: expatInsured.passportNumber ?? null
      }
    : undefined;

  const motorPolicy = values.motorDetails ?? values.motorDetails ?? {};

  const policyMotorInputs = isMotor
    ? {
        acalCategory: motorPolicy.acalCategory ?? null,
        bodyType: motorPolicy.body ?? null,
        chassis: motorPolicy.chassis ?? null,
        color: motorPolicy.color ?? null,
        engine: motorPolicy.engine ?? null,
        engineSize: motorPolicy.engineSize ?? null,
        engineType: motorPolicy.engineType ?? null,
        horsePower: motorPolicy.horsePower ?? null,
        make: motorPolicy.make ?? null,
        model: motorPolicy.model,
        nameOnLicenseAR: motorPolicy.nameOnLicenseAR ?? null,
        nameOnLicenseEN: motorPolicy.nameOnLicenseEN ?? null,
        plateCode: motorPolicy.plateCode ?? null,
        plateNumber: motorPolicy.plateNumber ?? null,
        scratching: motorPolicy.scratching ?? null,
        seatingCapacity: motorPolicy.seatingCapacity ?? null,
        sosService: motorPolicy.sosService ?? null,
        usageType: motorPolicy.usage ?? null,
        vignette: motorPolicy.vignette ?? null,
        vignetteCode: motorPolicy.vignetteCode ?? null,
        weight: motorPolicy.weight ?? null,
        yearOfMake: motorPolicy.yearOfMake ?? null,
        carValue: Number(motorPolicy.carValue) ?? null,
        agencyRepairYears: motorPolicy.agencyRepairYears ?? null,
        depreciationYears: motorPolicy.depreciationYears ?? null
      }
    : undefined;

  const medicalPolicy = values.medicalDetails ?? values.medicalDetails ?? {};

  const policyMedicalInputs = isMedical
    ? {
        grLimitDays: medicalPolicy.grLimitDays ?? 0,
        levelId: medicalPolicy.level ?? null,
        medicalClassId: medicalPolicy.class ?? null,
        nssf: medicalPolicy.nssf ?? null,
        numberOfAdherent: values?.medicalInsureds.length ?? 0
      }
    : undefined;

  const travelPolicy = values.travelDetails ?? values.travelDetails ?? {};
  const insuredPersonCount = persons.filter((p) => p.type === 'INSURED').length;

  const policyTravelInputs = isTravel
    ? {
        departureID: travelPolicy.departureId ?? null,
        destinationID: travelPolicy.destinationId ?? null,
        duration: Number(travelPolicy.duration) ?? 0,
        numberOfAdherent: insuredPersonCount,
        policyEndDate: new Date(values.policyEndDate) ?? null
      }
    : undefined;

  const coversInput =
    isMedical || isGroupMedical
      ? values.medicalDetails?.covers?.map((item: any) => ({
          coverID: item.coverID,
          isMandatory: item.isMandatory
        }))
      : isTravel
        ? values.travelDetails?.covers?.map((item: any) => ({
            coverID: item.coverID,
            isMandatory: item.isMandatory
          }))
        : undefined;

  const policyReinsuranceSubstandard =
    values.policyReinsuranceSubstandard ?? null;
  const policyReinsuranceSubstandardLimit =
    policyReinsuranceSubstandard === 'YES'
      ? numOrNull(values.policyReinsuranceSubstandardLimit)
      : null;

  return {
    id,
    input: {
      persons,
      policyPlanCoverInputs,
      policyQuoteInput,
      policyMarineInputs,
      policyMarineHullInputs,
      policyBankersBlanketBondInputs,
      policyPropertyInputs,
      policyGenericInputs,
      policyExpatInputs,
      policyMotorInputs,
      policyMedicalInputs,
      policyTravelInputs,
      coversInput,
      policyCurrencyID,
      policyEffectiveDate,
      policyEndDate,
      policyIssueDate,
      policyReinsuranceSubstandard,
      policyReinsuranceSubstandardLimit
    }
  };
}
